import CustomMediaRecorder from "./MediaRecorder/CustomMediaRecorder";

// ModernChunkedMediaRecorder
// What is the recording loop with two recorders?
// Take the active recorder and start recording
// Set the on data available handler to accumulate to a chunks list
// Set the on stop handler to set a stopped flag, and restart a recording loop on the next recorder
// Set a stop timeout 10s from now
// Set a condition in the on data available handler to call chunkReady if
// stopped condition is set
// Initialize the next recorder

class ModernChunkedMediaRecorder {
    constructor(mediaStream, chunkDurationMillis) {
        this.stream = mediaStream;
        this.chunkDurationMs = chunkDurationMillis;
        this.activeRecorder = null;
        this.nextRecorder = new CustomMediaRecorder(mediaStream, this.chunkDurationMs);
        this.isHDQuality = false;
        this.is4KQuality = false;
        this.onChunkReady = null;
        this.stopped = false;
        this.onFinalFlush = null;
    }

    async start(onChunkReady, isHDQuality = false, is4KQuality = false) {
        this.isHDQuality = isHDQuality;
        this.is4KQuality = is4KQuality;
        this.onChunkReady = onChunkReady;
        this.stopped = false;
        await this._startRecordingLoop();
    }

    stop(onFinalFlush) {
        this.stopped = true;
        this.onFinalFlush = onFinalFlush;

        if (this.activeRecorder) {
            this.activeRecorder.stop();
        }
        if (this.nextRecorder) {
            this.nextRecorder.stop();
        }
    }

    replaceVideoTrack(track) {
        if (this.activeRecorder) {
            this.activeRecorder.replaceVideoTrack(track);
        }
        if (this.nextRecorder) {
            this.nextRecorder.replaceVideoTrack(track);
        }
    }

    _destroyRecorder(recorder) {
        if (recorder && typeof recorder.destroy === 'function') {
            recorder.destroy();
        }
    }

    async _startRecordingLoop() {
        this.activeRecorder = this.nextRecorder;
        this.nextRecorder = null;

        let chunks = [];
        let resolveFinalData = null;
        const waitForFinalData = new Promise((resolve) => {
            resolveFinalData = resolve;
        });

        this.activeRecorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) {
                chunks.push(e.data);
            }
            if (resolveFinalData) {
                resolveFinalData();
                resolveFinalData = null;
            }
        };

        this.activeRecorder.onstop = async () => {
            await waitForFinalData;

            let finalBlob = new Blob(chunks, { type: "video/webm" });

            if (this.onChunkReady) {
                await this.onChunkReady(finalBlob);
            }

            chunks = null;
            finalBlob = null;

            this._destroyRecorder(this.activeRecorder);
            this.activeRecorder = null;

            if (!this.stopped) {
                this.nextRecorder = new CustomMediaRecorder(this.stream, this.chunkDurationMs);
                await this._startRecordingLoop();
            } else {
                this._destroyRecorder(this.nextRecorder);
                this.nextRecorder = null;

                if (this.onFinalFlush) {
                    this.onFinalFlush();
                    this.onFinalFlush = null;
                }
            }
        };

        await this.activeRecorder.start(this.isHDQuality, this.is4KQuality);

        setTimeout(() => {
            if (this.activeRecorder) {
                this.activeRecorder.requestData();
                this.activeRecorder.stop();
            }
        }, this.chunkDurationMs);
    }
}

export default ModernChunkedMediaRecorder;